import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { AccommodationService } from '../../features/accommodation/services/accommodation.service';
import { AllocateEmployeeDialog } from '../accommodation/allocate-employee/allocate-employee.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-accommodation-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    MatMenuModule,
    MatIconModule,
    NgxMatSelectSearchModule,
    MatTabsModule,
  ],
  templateUrl: './accommodation-details.component.html',
  styleUrl: './accommodation-details.component.scss',
})
export class AccommodationDetailsComponent implements OnInit {
  public loading: boolean = true;
  public error: any = null;
  private searchSubject = new Subject<string>();
  readonly dialog = inject(MatDialog);
  startDate: any;
  endDate: any;
  accommodationData: any;
  employeeData: any;
  upcomingAccommodationData: any;
  upcomingEmployeeData: any;
  dateRangeForm: FormGroup;
  canListAllocations$ = this.permissionService.hasPermission(
        ModuleIds.Accommodation,
        'List Allocations'
      );
    canEditAllocations$ = this.permissionService.hasPermission(
        ModuleIds.Accommodation,
        'Edit Allocation'
      );
      canReleaseAllocations$ = this.permissionService.hasPermission(
        ModuleIds.Accommodation,
        'Delete Allocation'
      );
      canAllocateAllocations$ = this.permissionService.hasPermission(
        ModuleIds.Accommodation,
        'Allocate'
      );
      canViewLogsAllocations$ = this.permissionService.hasPermission(
        ModuleIds.Accommodation,
        'View Logs'
      );

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date ? date <= today : false;
  };

  page = 0;
  pageSize = 10;
  totalPages = 1;
  pageSizeOptions = [5, 10, 20, 50, 100];
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];

  public paginationPageSize = 10;
  accommodationId: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private accommodationService: AccommodationService,
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getEmployeesInProperties(searchTerm);
        this.getUpcomingEmployeesInProperties(searchTerm);
      });

    this.dateRangeForm = this.fb.group({
      startDate: [''],
      endDate: [''],
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      this.accommodationId = queryParams.get('accommodationId');
      this.getEmployeesInProperties();
      this.getUpcomingEmployeesInProperties();
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.pageSize = 10;
    this.page = 0;
    this.searchSubject.next(searchTerm);
  }

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.getEmployeesInProperties('', this.pageSize);
      this.getUpcomingEmployeesInProperties('', this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.getEmployeesInProperties('', this.pageSize);
      this.getUpcomingEmployeesInProperties('', this.pageSize);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    console.log('New Page Size:', newSize);
    this.pageSize = newSize;
    this.page = 0;
    this.getEmployeesInProperties('', this.pageSize);
    this.getUpcomingEmployeesInProperties('', this.pageSize);
  }

  onDateChange(event: any, type: string) {
    const formattedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    if (type === 'start') {
      this.startDate = formattedDate;
    } else if (type === 'end') {
      this.endDate = formattedDate;
    }
    if (this.startDate != null && this.endDate != null) {
      this.getEmployeesInProperties();
      this.getUpcomingEmployeesInProperties();
    }
  }

  getEmployeesInProperties(search: string = '', pageSize: number = 10) {
    this.loading = true;
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;

    const input = {
      propertyId: Number(this.accommodationId),
      limit: this.pageSize,
      skip: skip,
      search: search,
      startDate: this.startDate,
      endDate: this.endDate,
      statusFilter: 'Ongoing',
    };
    this.accommodationService.getEmployeesInProperties(input).subscribe({
      next: (data) => {
        this.totalPages = Math.ceil(
          (data?.total || data.propertyAllocations?.length) / this.pageSize
        );
        this.loading = false;
        this.accommodationData = data?.propertyAllocations[0];
        this.employeeData = data?.propertyAllocations[0]?.employees || [];
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getUpcomingEmployeesInProperties(search: string = '', pageSize: number = 10) {
    this.loading = true;
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;

    const input = {
      propertyId: Number(this.accommodationId),
      limit: this.pageSize,
      skip: skip,
      search: search,
      startDate: this.startDate,
      endDate: this.endDate,
      statusFilter: 'Upcoming',
    };
    this.accommodationService.getEmployeesInProperties(input).subscribe({
      next: (data) => {
        this.totalPages = Math.ceil(
          (data?.total || data.propertyAllocations?.length) / this.pageSize
        );
        this.loading = false;
        this.upcomingAccommodationData = data?.propertyAllocations[0];
        this.upcomingEmployeeData =
          data?.propertyAllocations[0]?.employees || [];
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onClear() {
    this.startDate = null;
    this.endDate = null;
    this.dateRangeForm.reset();
    this.getEmployeesInProperties();
    this.getUpcomingEmployeesInProperties();
  }

  navigateTo(route: string) {
    this.router.navigate([route], {
      queryParams: { accommodationId: this.accommodationId },
    });
  }

  isDropdownOpen = false;

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  // allocate employee dialog

  openAddDialog() {
    const dialogRef = this.dialog.open(AllocateEmployeeDialog, {
      disableClose: true,
      data: {
        propertyData: this.accommodationData,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.getEmployeesInProperties();
        this.getUpcomingEmployeesInProperties();
      }
    });
  }

  openEditDialog(data: any, releaseEmployee: Boolean) {
    const employeeData = {
      employeeId: data?.employeeId,
      startDate: data?.startDate,
      endDate: data?.endDate,
      allocationId: data?.allocationId,
    };
    const dialogRef = this.dialog.open(AllocateEmployeeDialog, {
      disableClose: true,
      data: {
        propertyData: this.accommodationData,
        employeeData: employeeData,
        releaseEmployee: releaseEmployee,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.getEmployeesInProperties();
        this.getUpcomingEmployeesInProperties();
      }
    });
  }
}
